import React, { useContext, useMemo } from 'react';
import { Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import EChartsReact from 'echarts-for-react';
import AppContext from 'context/Context';
import { getColor, themeColors } from 'helpers/utils';

const DepartmentSpendingBudgetChart = ({ summary }) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const periodLabels = summary?.periodLabels || [];
  const periods = summary?.totalRow?.periods || [];
  const periodProfitAndLoss = periods.map(period => period?.profitAndLoss ?? 0);
  const periodBudgets = periods.map(period => period?.budget ?? 0);

  const hasData = periodLabels.length > 0;

  const options = useMemo(
    () => ({
      grid: {
        left: '3%',
        right: '4%',
        bottom: periodLabels.length > 6 ? '16%' : '8%',
        top: '22%',
        containLabel: true
      },
      legend: {
        top: 0,
        textStyle: { color: isDark ? 'white' : '#000000' }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        valueFormatter: value => `£${Number(value).toLocaleString('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`
      },
      xAxis: {
        type: 'category',
        data: periodLabels,
        axisLine: { lineStyle: { color: isDark ? '#4a4f58' : '#c4c9d0' } },
        axisLabel: {
          color: isDark ? 'white' : '#000000',
          rotate: periodLabels.length > 6 ? 45 : 0
        }
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          color: isDark ? 'white' : '#000000',
          formatter: value => `£${value}`
        },
        splitLine: { lineStyle: { color: isDark ? '#2c3c4f' : '#edf2f9' } }
      },
      series: [
        {
          name: 'Profit and Loss',
          type: 'bar',
          data: periodProfitAndLoss,
          barGap: '10%',
          itemStyle: {
            color: getColor('primary'),
            borderRadius: [4, 4, 0, 0]
          }
        },
        {
          name: 'Budget',
          type: 'bar',
          data: periodBudgets,
          itemStyle: {
            color: themeColors.secondary,
            borderRadius: [4, 4, 0, 0]
          }
        }
      ]
    }),
    [periodLabels, periodProfitAndLoss, periodBudgets, isDark]
  );

  return (
    <>
      <Row className="ms-2 mt-3 mb-0 pb-0">
        <Col>
          <span className="p-2 h5 float-start">Profit and Loss vs Budget</span>
        </Col>
      </Row>
      {hasData ? (
        <EChartsReact option={options} style={{ height: '260px' }} />
      ) : (
        <div className="text-center text-muted p-5">No spending data available.</div>
      )}
    </>
  );
};

DepartmentSpendingBudgetChart.propTypes = {
  summary: PropTypes.shape({
    periodLabels: PropTypes.arrayOf(PropTypes.string),
    totalRow: PropTypes.shape({
      periods: PropTypes.arrayOf(
        PropTypes.shape({
          profitAndLoss: PropTypes.number,
          budget: PropTypes.number
        })
      )
    })
  })
};

export default DepartmentSpendingBudgetChart;
